import { LoginResponse, User } from "../features/Authentication/types/auth.types"
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'

type CurrentUser = User

export interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresIn: number
  expiresAt?: number
}

interface JwtPayload {
  exp?: number
  [key: string]: any
}

class TokenService {
  private readonly AUTH_TOKEN_KEY = 'auth_tokens'
  private readonly USER_DATA_KEY = 'user_data'

  /**
   * Store authentication tokens in both localStorage and cookies
   * Using cookies for SSR/middleware and localStorage for client-side access
   */
  public setTokens(
    accessToken: string,
    refreshToken: string,
    expiresIn: number
  ): void {
    const tokens: AuthTokens = {
      accessToken,
      refreshToken,
      expiresIn,
      expiresAt: Date.now() + expiresIn * 1000,
    }

    if (typeof window !== 'undefined') {
      localStorage.setItem(this.AUTH_TOKEN_KEY, JSON.stringify(tokens))
    }

    const maxAge = Math.min(expiresIn, 30 * 24 * 60 * 60) // 30 days in seconds

    Cookies.set(this.AUTH_TOKEN_KEY, JSON.stringify(tokens), {
      expires: new Date(Date.now() + maxAge * 1000),
      path: '/',
      sameSite: 'strict',
      // In production, you'd want to use secure cookies
      secure: process.env.NODE_ENV === 'production',
    })
  }

  public setUserData(loginResponse: LoginResponse): void {
    const currentUser: User = {
      id: loginResponse.user.id,
      clientId: loginResponse.user.clientId,
      email: loginResponse.user.email,
      username: loginResponse.user.username,
      name: loginResponse.user.username
    }

    if (typeof window !== 'undefined') {
      localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(currentUser))
    }

    const maxAge = 30 * 24 * 60 * 60 // 30 days in seconds

    Cookies.set(this.USER_DATA_KEY, JSON.stringify(currentUser), {
      expires: new Date(Date.now() + maxAge * 1000),
      path: '/',
      sameSite: 'strict',
      secure: process.env.NODE_ENV === 'production',
    })
  }
  /**
   * Get the stored access token
   */
  public getAccessToken(): string | null {
    // First try localStorage
    if (typeof window !== 'undefined') {
      const tokensStr = localStorage.getItem(this.AUTH_TOKEN_KEY)
      if (tokensStr) {
        try {
          const tokens = JSON.parse(tokensStr) as AuthTokens
          return tokens.accessToken
        } catch (error) {
          console.error('Error parsing access token from localStorage:', error)
        }
      }
    }

    // Fallback to cookies
    const cookieTokens = Cookies.get(this.AUTH_TOKEN_KEY)
    if (cookieTokens) {
      try {
        const tokens = JSON.parse(cookieTokens) as AuthTokens
        return tokens.accessToken
      } catch (error) {
        console.error('Error parsing access token from cookies:', error)
      }
    }

    return null
  }

  public getClientId(): string {
    if (typeof window !== 'undefined') {
      const currentUserStr = localStorage.getItem(this.USER_DATA_KEY)
      if (currentUserStr) {
        try {
          const currentUser = JSON.parse(currentUserStr) as CurrentUser

          return currentUser.clientId
        } catch (error) {
          console.error('Error parsing access token from localStorage:', error)
        }
      }
    }

    const cookieTokens = Cookies.get(this.USER_DATA_KEY)
    if (cookieTokens) {
      try {
        const tokens = JSON.parse(cookieTokens) as CurrentUser
        return tokens.clientId
      } catch (error) {
        console.error('Error parsing access token from cookies:', error)
      }
    }

    window.location.reload()

    return ''
  }
  public getUserData(): CurrentUser | null {
    if (typeof window !== 'undefined') {
      const currentUserStr = localStorage.getItem(this.USER_DATA_KEY)
      if (currentUserStr) {
        try {
          return JSON.parse(currentUserStr) as CurrentUser
        } catch (error) {
          console.error('Error parsing user data from localStorage:', error)
        }
      }
    }

    const cookieCurrentUser = Cookies.get(this.USER_DATA_KEY)
    if (cookieCurrentUser) {
      try {
        return JSON.parse(cookieCurrentUser) as CurrentUser
      } catch (error) {
        console.error('Error parsing user data from cookies:', error)
      }
    }

    return null
  }
  /**
   * Get the stored refresh token
   */
  public getRefreshToken(): string | null {
    if (typeof window !== 'undefined') {
      const tokensStr = localStorage.getItem(this.AUTH_TOKEN_KEY)
      if (tokensStr) {
        try {
          const tokens = JSON.parse(tokensStr) as AuthTokens
          return tokens.refreshToken
        } catch (error) {
          console.error('Error parsing refresh token from localStorage:', error)
        }
      }
    }

    const cookieTokens = Cookies.get(this.AUTH_TOKEN_KEY)
    if (cookieTokens) {
      try {
        const tokens = JSON.parse(cookieTokens) as AuthTokens
        return tokens.refreshToken
      } catch (error) {
        console.error('Error parsing refresh token from cookies:', error)
      }
    }

    return null
  }

  /**
   * Check if the user is authenticated (has a valid token)
   */
  public isAuthenticated(): boolean {
    const accessToken = this.getAccessToken()
    if (!accessToken) return false

    try {
      const decoded = jwtDecode<JwtPayload>(accessToken)

      if (decoded.exp) {
        return decoded.exp * 1000 > Date.now()
      }

      return true
    } catch (error) {
      console.error('Error decoding token:', error)
      return false
    }
  }

  /**
   * Clear all authentication tokens
   */
  public clearTokens(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(this.AUTH_TOKEN_KEY)
    }

    Cookies.remove(this.AUTH_TOKEN_KEY, { path: '/' })
  }
}

export const tokenService = new TokenService()
export default tokenService
